import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bookmark,
  Sparkles,
  Lock,
  RotateCcw,
  X,
} from "lucide-react";
import { trackFP } from "../lib/firstPartyAnalytics";

/**
 * GuestSaveRoutineModal
 * ---------------------
 * Soft, value-framed account modal shown to GUEST users on /routine
 * after their personalized routine has rendered (Feb 2026 capture-
 * first redesign). This is the moment of maximum perceived value —
 * the user just saw a routine made for their face — so the ask is
 * framed around ownership, continuity, and personalization rather
 * than gating.
 *
 * Behavioural rules:
 *   • Only renders when `open` is true AND the page passes a routine
 *     (we don't ask people to "save" before they have something to
 *     save).
 *   • Primary CTA → /login (the registration sheet). Carries a
 *     `?next=` so the user lands back on this routine after the
 *     account exists.
 *   • "Maybe later" dismisses without guilt copy and never raises
 *     this modal again in the current session. The persistent
 *     "Keep my routine" CTA at the bottom of /routine remains for
 *     anyone who changes their mind.
 *   • Fires `signup_modal_*` engagement events via trackFP so the
 *     conversion loop (modal viewed → signup OR dismiss) is
 *     measurable end-to-end without disturbing the canonical
 *     10-step funnel.
 */
const GuestSaveRoutineModal = ({ open, onDismiss, routineId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fire `signup_modal_viewed` exactly once per open-cycle so the
  // dashboard can divide signups by modal-view to get a true
  // post-preview conversion rate.
  useEffect(() => {
    if (!open) return;
    trackFP("signup_modal_viewed", {
      source: "guest_save_routine",
      routine_id: routineId || null,
    });
  }, [open, routineId]);

  // Keyboard: ESC dismisses. Mirrors the close-button affordance so
  // the modal never feels trapped.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onDismiss]);

  const handleSave = () => {
    trackFP("signup_modal_save_clicked", {
      source: "guest_save_routine",
      routine_id: routineId || null,
    });
    // Carry the routine id through the auth flow so once the account
    // exists the user lands BACK on this exact routine, not the home
    // launchpad.
    const next = routineId ? `/routine/${routineId}` : "/my-routines";
    navigate(`/login?next=${encodeURIComponent(next)}&intent=save_routine`);
  };

  const handleDismiss = () => {
    trackFP("signup_modal_dismissed", {
      source: "guest_save_routine",
      routine_id: routineId || null,
    });
    onDismiss();
  };

  const bullets = [
    {
      key: "save",
      Icon: Bookmark,
      label: t("guestSaveModal.bulletSave"),
    },
    {
      key: "tailored",
      Icon: Sparkles,
      label: t("guestSaveModal.bulletTailored"),
    },
    {
      key: "private",
      Icon: Lock,
      label: t("guestSaveModal.bulletPrivate"),
    },
    {
      key: "return",
      Icon: RotateCcw,
      label: t("guestSaveModal.bulletReturn"),
    },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#2C2123]/55 backdrop-blur-sm px-4 sm:px-6 pb-4 sm:pb-0"
          role="dialog"
          aria-modal="true"
          aria-labelledby="guest-save-modal-title"
          data-testid="guest-save-routine-modal"
        >
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.32, ease: "easeOut" }}
            className="relative w-full max-w-md bg-white rounded-3xl p-7 sm:p-8 soft-shadow-lg"
          >
            <button
              type="button"
              onClick={handleDismiss}
              className="absolute top-4 right-4 h-9 w-9 rounded-full text-[#8A7D82] hover:text-[#2C2123] hover:bg-[#F5EFEB] flex items-center justify-center transition-colors"
              aria-label={t("guestSaveModal.closeLabel")}
              data-testid="guest-save-modal-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center mb-5 shadow-[0_6px_18px_rgba(155,106,108,0.28)]">
              <Bookmark className="h-5 w-5 text-white" />
            </div>

            <p className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-2">
              {t("guestSaveModal.kicker")}
            </p>
            <h2
              id="guest-save-modal-title"
              className="font-serif italic text-2xl sm:text-3xl text-[#2C2123] leading-tight mb-3"
              data-testid="guest-save-modal-title"
            >
              {t("guestSaveModal.title")}
            </h2>
            <p className="text-[#5C4B51] leading-relaxed mb-6">
              {t("guestSaveModal.body")}
            </p>

            <ul className="space-y-3 mb-7">
              {bullets.map((b) => (
                <li
                  key={b.key}
                  className="flex items-start gap-3 text-[#2C2123]"
                  data-testid={`guest-save-modal-bullet-${b.key}`}
                >
                  <span className="h-8 w-8 rounded-full bg-[#F8EEEE] flex items-center justify-center shrink-0">
                    <b.Icon className="h-4 w-4 text-[#9B6A6C]" />
                  </span>
                  <span className="text-sm leading-relaxed pt-1">
                    {b.label}
                  </span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3.5 text-base font-medium transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
                data-testid="guest-save-modal-cta"
              >
                {t("guestSaveModal.primaryCta")}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="text-sm text-[#8A7D82] hover:text-[#5C4B51] py-2 transition-colors"
                data-testid="guest-save-modal-dismiss"
              >
                {t("guestSaveModal.dismissCta")}
              </button>
            </div>

            <p className="mt-4 text-[11px] text-[#8A7D82] leading-relaxed text-center">
              {t("guestSaveModal.privacyLine")}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GuestSaveRoutineModal;
